import { Auth } from "aws-amplify";
import { Component, FunctionComponent } from "react";
import {
    Alert,
    Card,
    Col,
    Image,
    ListGroup,
    Modal,
    Row,
    Spinner,
} from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { IGetCourier, IRegisterAccount, CourierSummary, SetCourierContactDetails, ContactDetails } from "../../client/core";
import { Color, Path, Storage } from "../../env";
import { Button, variant } from "../form/Button";
import UpdateLogo from "../account/UpdateLogo";
import { Storage as S3 } from "aws-amplify";
import UpdateContact from "../account/UpdateContact";

export interface CourierDashboardState {
    error: string;
    courierSummary: CourierSummary;
    isLoading: boolean;
    activity: Activity;
}

export interface CourierDashboardProps extends RouteComponentProps {
    getCourierAPI: IGetCourier;
    registerCourierAPI: IRegisterAccount;
    auth: typeof Auth;
}

export default class CourierDashboard extends Component<
    CourierDashboardProps,
    CourierDashboardState
> {
    constructor(props: CourierDashboardProps) {
        super(props);
        this.state = {
            courierSummary: {
                name: "",
                address: "",
                accountStatus: "",
                deliveryStatus: "",
                logo: "",
                email: "",
                phone: "",
                whatsapp: "",
            },
            isLoading: true,
            error: "",
            activity: Activity.None,
        };
    }

    componentDidMount() {
        this.getCourierSummary();
    }

    logOut = () => {
        localStorage.clear();
        this.props.auth.signOut();
        return this.props.history.push(Path.Login);
    };

    getCourierSummary = () => {
        if (!localStorage.getItem(Storage.IsLoggedIn)) {
            return this.props.history.push(Path.Login);
        }

        this.props.auth.currentSession().then(async (session) => {
            let CourierID = localStorage.getItem(Storage.CourierID);

            // if CourierID is not present retrieve from API
            if (CourierID == null) {
                const token = session.getIdToken().getJwtToken();
                await this.props.getCourierAPI
                    .GetCourierID({
                        identityToken: token,
                    })
                    .then((output) => {
                        if (output.statusCode === 403) {
                            return this.logOut();
                        }
                        if (output.statusCode === 404) {
                            localStorage.removeItem(Storage.CourierID)
                            return this.props.history.push(Path.RegisterCourier);
                        }
                        if (output.statusCode !== 200) {
                            this.setState({
                                error: "Sorry, we were unable to find your Courier details",
                            });
                        }
                        localStorage.setItem(Storage.CourierID, output.courierID!);
                    })
                    .catch((err) =>
                        this.setState({
                            error: err.message,
                        })
                    );
                CourierID = localStorage.getItem(Storage.CourierID);
                if (CourierID == null) {
                    return;
                }
            }

            const token = session.getIdToken().getJwtToken();
            this.props.getCourierAPI
                .GetCourierSummary({
                    identityToken: token,
                    courierID: CourierID!,
                })
                .then((output) => {
                    if (output.statusCode !== 200) {
                        localStorage.clear();
                        this.props.auth.signOut();
                        return this.props.history.push(Path.Login);
                    }
                    localStorage.setItem(Storage.CourierName, output.courierSummary!.name)
                    this.setState({
                        isLoading: false,
                        courierSummary: output.courierSummary!,
                    });
                })
                .catch((err) => {
                    this.setState({
                        error: err.message,
                    });
                    localStorage.removeItem(Storage.CourierID);
                });
        });
    };

    registerAccount = () => {
        this.props.auth
            .currentSession()
            .then((session) => {
                const token = session.getIdToken().getJwtToken();
                this.props.registerCourierAPI
                    .RegisterAccount({
                        identityToken: token,
                        courierID: localStorage.getItem(Storage.CourierID)!,
                        refreshURL:
                            window.location.protocol +
                            "//" +
                            window.location.host +
                            Path.CourierDashboard,
                        returnURL:
                            window.location.protocol +
                            "//" +
                            window.location.host +
                            Path.CourierDashboard,
                    })
                    .then((output) => {
                        if (output.statusCode === 403) {
                            return this.props.history.push(Path.Login);
                        }

                        if (output.statusCode === 200) {
                            return window.location.replace(output.accountLink!);
                        }

                        this.setState({
                            error: "Sorry, we were unable to connect with our account partner Stripe. We are working to resolve the issue.",
                        });
                    })
                    .catch(() =>
                        this.setState({
                            error: "Sorry, we were unable to connect with our account partner Stripe. We are working to resolve the issue.",
                        })
                    );
            })
            .catch(() => {
                this.props.history.push(Path.Login);
            });
    };

    navigate = (path: Path) => {
        this.props.history.push(path);
    };

    changeActivity = (activity: Activity) => {
        this.setState({
            activity: activity,
        })
    }

    updateLogo = (image: any) => {
        const { courierSummary: CourierSummary } = this.state
        this.setState({
            courierSummary: {
                ...CourierSummary,
                logo: image,
            }
        })
    }

    updateContact = (contact: ContactDetails) => {
        const { courierSummary } = this.state
        courierSummary.email = contact.email
        courierSummary.phone = contact.phone
        courierSummary.whatsapp = contact.whatsapp
        this.setState({
            courierSummary: courierSummary
        })
    }

    render() {
        const props: CourierSummaryCardProps = {
            ...this.props,
            name: this.state.courierSummary.name,
            address: this.state.courierSummary.address,
            accountStatus: this.state.courierSummary.accountStatus,
            deliveryStatus: this.state.courierSummary.deliveryStatus,
            error: this.state.error,
            isLoading: this.state.isLoading,
            courierID: localStorage.getItem(Storage.CourierID) || "",
            logo: this.state.courierSummary.logo,
            activity: this.state.activity,
            email: this.state.courierSummary.email,
            phone: this.state.courierSummary.phone,
            whatsapp: this.state.courierSummary.whatsapp || "-",
            updateLogo: this.updateLogo,
            registerAccount: this.registerAccount,
            navigate: this.navigate,
            changeActivity: this.changeActivity,
            updateContact: this.updateContact,
        };
        return <CourierSummaryCard {...props} />;
    }
}

export interface CourierSummaryCardProps extends RouteComponentProps {
    activity: Activity;
    name: string;
    address: string;
    accountStatus: string;
    deliveryStatus: string;
    error: string;
    isLoading: boolean;
    courierID?: string;
    logo: string;
    email: string;
    phone: string;
    whatsapp?: string;
    navigate: (path: Path) => void;
    registerAccount: () => void;
    changeActivity: (activity: Activity) => void;
    updateLogo: (image: any) => void;
    updateContact: (contact: ContactDetails) => void;
}

export enum Activity {
    None,
    UpdateLogo,
    UpdateAlias,
    UpdateContact,
}

export const CourierSummaryCard: FunctionComponent<CourierSummaryCardProps> = (
    props
) => (
    <>
        <Modal
            show={props.activity == Activity.UpdateLogo}
            onHide={() => props.changeActivity(Activity.None)}
        >
            <Modal.Header>
                <Modal.Title>Update Logo</Modal.Title>
                <button type="button" className="close" aria-label="Close" onClick={() => props.changeActivity(Activity.None)}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <UpdateLogo postSubmit={img => {
                    props.changeActivity(Activity.None)
                    props.updateLogo(img)
                }} imageStore={S3} fileReader={new FileReader()} />
            </Modal.Body>
        </Modal>
        <Modal
            show={props.activity == Activity.UpdateContact}
            onHide={() => props.changeActivity(Activity.None)}
        >
            <Modal.Header>
                <Modal.Title>Change Shop Contact</Modal.Title>
                <button type="button" className="close" aria-label="Close" onClick={() => props.changeActivity(Activity.None)}>
                    <span aria-hidden="true">&times;</span>
                </button>
            </Modal.Header>
            <Modal.Body>
                <UpdateContact {...props} setCourierContactDetailsAPI={new SetCourierContactDetails()} auth={Auth} postSubmit={contact => {
                    props.updateContact(contact)
                    props.changeActivity(Activity.None)
                }} />
            </Modal.Body>
        </Modal>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        {props.isLoading ? (
            <div style={{ textAlign: "center" }}><Spinner variant="success" animation="border" /></div>
        ) : (
            <>
                {props.name && (
                    <Row>
                        <Col xs={12} style={{ marginBottom: "3rem" }}>
                            <h1>Dashboard</h1>
                            <Col xs={12} xl={6} xxl={4}>
                                <div style={{ width: "250px", textAlign: "center" }}>
                                    <Image style={{ display: "block", padding: "1rem" }} width="100%" src={props.logo || "https://via.placeholder.com/350x350.png?text=Logo"} rounded />
                                    <Button name={"Update Logo"} onClick={() => {
                                        props.changeActivity(Activity.UpdateLogo)
                                    }} variant={variant.Primary} />
                                </div>
                            </Col>
                        </Col>
                        <Col xs={12} xl={6} xxl={4}>
                            <Row>
                                <Col xs={12}>
                                    <h5>Summary</h5>
                                    <ListGroup as="ol" style={{ marginBottom: "2rem", borderTop: `1px solid ${Color.Grey}`, borderRadius: 0 }}>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">Name</div>
                                                {props.name}
                                            </div>
                                        </ListGroup.Item>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">Address</div>
                                                {props.address}
                                            </div>
                                        </ListGroup.Item>
                                    </ListGroup>
                                    <h5>Contact</h5>
                                    <ListGroup as="ol" style={{ marginBottom: "2rem", borderTop: `1px solid ${Color.Grey}`, borderRadius: 0 }}>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">Email</div>
                                                {props.email}
                                            </div>
                                            <a onClick={() => props.changeActivity(Activity.UpdateContact)} style={{ color: Color.Primary, cursor: "pointer" }}>Edit</a>
                                        </ListGroup.Item>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">Phone</div>
                                                {props.phone}
                                            </div>
                                            <a onClick={() => props.changeActivity(Activity.UpdateContact)} style={{ color: Color.Primary, cursor: "pointer" }}>Edit</a>
                                        </ListGroup.Item>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">WhatApp Number</div>
                                                {props.whatsapp}
                                            </div>
                                            <a onClick={() => props.changeActivity(Activity.UpdateContact)} style={{ color: Color.Primary, cursor: "pointer" }}>Edit</a>
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={12} xl={6} xxl={4}>
                            <Row>
                                <Col>
                                    <h5>Partnership</h5>
                                    <ListGroup as="ol" style={{ marginBottom: "2rem", borderTop: `1px solid ${Color.Grey}`, borderRadius: 0 }}>
                                        <ListGroup.Item
                                            as="li"
                                            className="d-flex justify-content-between align-items-start"
                                            style={{ border: 0 }}
                                        >
                                            <div className="ms-2 me-auto">
                                                <div className="fw-bold">Referral Link</div>
                                                {`https://dashboard.${process.env.REACT_APP_BASE_DOMAIN}/referral?referrer=${props.courierID}`}
                                            </div>
                                            <a onClick={() => navigator.clipboard.writeText(`https://dashboard.${process.env.REACT_APP_BASE_DOMAIN}/referral?referrer=${props.courierID}`)} style={{ color: Color.Primary, cursor: "pointer" }}>Copy</a>
                                        </ListGroup.Item>
                                    </ListGroup>
                                </Col>
                            </Row>
                        </Col>
                    </Row>

                )}
                <Actions {...props} />
            </>
        )}
    </>
);

export const Actions: FunctionComponent<CourierSummaryCardProps> = (props) => (
    <>
        {(props.name == "" || props.address == "" || props.accountStatus != "OK" || props.deliveryStatus != "OK") && (
            <p style={{ textAlign: "center", marginTop: "1rem" }}>Required Actions</p>
        )}
        {props.name == "" && (
            <Card>
                <Card.Body>
                    <Card.Text>Please register your company name.</Card.Text>
                    <Button
                        variant={variant.Primary}
                        id="register-name-btn"
                        name={"Register"}
                        onClick={() => props.navigate(Path.RegisterCourierName)}
                    />
                </Card.Body>
            </Card>
        )}
        {props.address === "" && (
            <Card>
                <Card.Body>
                    <Card.Text>Please register your company address.</Card.Text>
                    <Button
                        variant={variant.Primary}
                        id="register-address-btn"
                        name={"Register"}
                        onClick={() => props.navigate(Path.RegisterCourierAddress)}
                    />
                </Card.Body>
            </Card>
        )}
        {props.accountStatus.toUpperCase() !== "OK" && (
            <Card>
                <Card.Body>
                    <Card.Text>Our payment partner is currently reviewing your account details which should only take a couple of days. Meanwhile, you can view or edit your account details by selecting the button below. Please ensure all required details filled in correctly.</Card.Text>
                    <Button
                        variant={variant.Primary}
                        id="account-details-btn"
                        name={"Account Details"}
                        onClick={() => props.registerAccount()}
                    />
                </Card.Body>
            </Card>
        )}
        {props.deliveryStatus !== "OK" && (
            <Card>
                <Card.Body>
                    <Card.Text>Please set your delivery dates.</Card.Text>
                    <Button
                        variant={variant.Primary}
                        id="delivery-dates-btn"
                        name={"Go"}
                        onClick={() => props.navigate(Path.Delivery)}
                    />
                </Card.Body>
            </Card>
        )}
    </>
);
